import Vector2 from "../math/Vector2";
import isMobile from "ismobilejs";

/**
 * @class
 * @memberOf P3
 */
export default class Input {
    constructor() {
        /**
         * @member {Object} signals
         * @member {signals.Signal} signals.mouseMove
         * @member {signals.Signal} signals.mouseDown
         * @member {signals.Signal} signals.mouseUp
         * @member {signals.Signal} signals.mouseClick
         * @member {signals.Signal} signals.keyDown
         * @member {signals.Signal} signals.keyUp
         * @member {signals.Signal} signals.swipeLeft
         * @member {signals.Signal} signals.swipeRight
         * @member {signals.Signal} signals.swipeUp
         * @member {signals.Signal} signals.swipeDown
         * @member {signals.Signal} signals.orientation
         */
        this.signals = {
            mouseMove: new signals.Signal(),
            mouseDown: new signals.Signal(),
            mouseUp: new signals.Signal(),
            mouseClick: new signals.Signal(),
            keyDown: new signals.Signal(),
            keyUp: new signals.Signal(),
            swipeLeft: new signals.Signal(),
            swipeRight: new signals.Signal(),
            swipeUp: new signals.Signal(),
            swipeDown: new signals.Signal(),
            orientation: new signals.Signal()
        };

        /**
         * @member {PIXI.DisplayObject}
         * @private
         */
        this._stage = null;

        /**
         * @member {Object}
         * @private
         */
        this._keyMap = {};

        /**
         * @member {Object}
         * @private
         */
        this._keyBuffer = {};

        /**
         * @member {PIXI.Point}
         * @private
         */
        this._touch = null;

        /**
         * @type {null}
         * @private
         */
        this._swipeStart = null;

        /**
         * @type {number}
         * @private
         */
        this._swipeTime = 0;

        /**
         * @member {Object} orientation
         * @member {number} orientation.gamma
         * @member {number} orientation.beta
         * @member {number} orientation.alpha
         * @private
         */
        this._orientation = {
            gamma: 0,
            beta: 0,
            alpha: 0
        };
    }

    /**
     * @param {PIXI.DisplayObject} stage
     * @param {*} keyListener
     */
    init(stage, keyListener) {
        this._stage = stage;
        this._stage.interactive = true;
        this._stage.mousemove = this._stage.touchmove = this._onMouseMove.bind(this);
        this._stage.mousedown = this._stage.touchstart = this._onMouseDown.bind(this);
        this._stage.mouseup = this._stage.touchend = this._onMouseUp.bind(this);
        this._stage.click = this._stage.tap = this._onMouseClick.bind(this);

        keyListener.onkeydown = this._onKeyDown.bind(this);
        keyListener.onkeyup = this._onKeyUp.bind(this);
        keyListener.focus();

        document.onclick = function() {
            keyListener.focus();
        };

        isMobile.any && window.addEventListener("deviceorientation", this._onDeviceOrientation.bind(this));
    }

    /**
     */
    destroy() {
        this._stage.mousemove = this._stage.touchmove = null;
        this._stage.mousedown = this._stage.touchstart = null;
        this._stage.mouseup = this._stage.touchend = null;
        this._stage.click = this._stage.tap = null;

        isMobile.any && window.removeEventListener("deviceorientation", this._onDeviceOrientation);

        this.signals.mouseMove.dispose();
        this.signals.mouseDown.dispose();
        this.signals.mouseUp.dispose();
        this.signals.mouseClick.dispose();

        this.signals.keyDown.dispose();
        this.signals.keyUp.dispose();

        this.signals.swipeLeft.dispose();
        this.signals.swipeRight.dispose();
        this.signals.swipeUp.dispose();
        this.signals.swipeDown.dispose();

        this.signals.orientation.dispose();
    }

    /**
     */
    update() {
        for (let i in this._keyBuffer) {
            if (this._keyBuffer.hasOwnProperty(i)) {
                this._keyBuffer[i] = false;
            }
        }
    }

    /**
     * @param {number} keyCode
     * @returns {boolean}
     */
    isKeyDown(keyCode) {
        return this._keyMap[keyCode] === true;
    }

    /**
     * @param {number} keyCode
     * @returns {boolean}
     */
    isKeyPressed(keyCode) {
        return this._keyBuffer[keyCode] === true;
    }

    /**
     * @param {Event} event
     * @private
     */
    _onMouseMove(event) {
        this._touch && (this._touch = event.data.getLocalPosition(this._stage));

        this.signals.mouseMove.dispatch(event);
    }

    /**
     * @param {Event} event
     * @private
     */
    _onMouseDown(event) {
        this._touch = event.data.getLocalPosition(this._stage);

        if (isMobile.any) {
            this._swipeStart = this._touch.clone();
            this._swipeTime = window.performance.now();
        }
        this.signals.mouseDown.dispatch(event);
    }

    /**
     * @param {Event} event
     * @private
     */
    _onMouseUp(event) {
        if (isMobile.any) {
            const swipe = new Vector2(this._touch.x - this._swipeStart.x, this._touch.y - this._swipeStart.y);
            const distance = swipe.lengthSq;
            const minimum = 4 * 4;
            swipe.normalize();

            if (window.performance.now() - this._swipeTime < 800 && distance > minimum) {
                const left = swipe.dot(new Vector2(-1, 0));
                (left > 0.9) && this.signals.swipeLeft.dispatch();

                const right = swipe.dot(new Vector2(1, 0));
                (right > 0.9) && this.signals.swipeRight.dispatch();

                const up = swipe.dot(new Vector2(0, -1));
                (up > 0.9) && this.signals.swipeUp.dispatch();

                const down = swipe.dot(new Vector2(0, 1));
                (down > 0.9) && this.signals.swipeDown.dispatch();
            }
        }

        this._touch = null;
        this.signals.mouseUp.dispatch(event);
    }

    /**
     * @param {Event} event
     * @private
     */
    _onMouseClick(event) {
        this.signals.mouseClick.dispatch(event);
    }

    /**
     * @param {Event} event
     * @private
     */
    _onKeyDown(event) {
        if (event.keyCode === Input.keys.LEFT || event.keyCode === Input.keys.RIGHT ||
            event.keyCode === Input.keys.UP || event.keyCode === Input.keys.DOWN ||
            event.keyCode === Input.keys.SPACE) event.preventDefault();

        !this.isKeyDown(event.keyCode) && (this._keyBuffer[event.keyCode] = true);
        this._keyMap[event.keyCode] = true;
        this.signals.keyDown.dispatch(event);
    }

    /**
     * @param {Event} event
     * @private
     */
    _onKeyUp(event) {
        if (event.keyCode === Input.keys.LEFT || event.keyCode === Input.keys.RIGHT ||
            event.keyCode === Input.keys.UP || event.keyCode === Input.keys.DOWN ||
            event.keyCode === Input.keys.SPACE) event.preventDefault();

        this._keyMap[event.keyCode] = false;
        this.signals.keyUp.dispatch(event);
    }

    /**
     * @param {Event} event
     * @private
     */
    _onDeviceOrientation(event) {
        this.signals.orientation.dispatch(event);
    }

    /**
     * @returns {PIXI.Point}
     */
    get touch() {
        return this._touch;
    }

    /**
     * @returns {Object} orientation
     * @returns {number} orientation.gamma
     * @returns {number} orientation.beta
     * @returns {number} orientation.alpha
     */
    get orientation() {
        return this._orientation;
    }
}

/**
 * @static
 * @constant
 * @type {Object}
 */
Input.keys = {};

/**
 * @static
 * @constant
 * @type {number}
 */
Input.keys.W = 87;

/**
 * @static
 * @constant
 * @type {number}
 */
Input.keys.A = 65;

/**
 * @static
 * @constant
 * @type {number}
 */
Input.keys.S = 83;

/**
 * @static
 * @constant
 * @type {number}
 */
Input.keys.D = 68;

/**
 * @static
 * @constant
 * @type {number}
 */
Input.keys.Z = 90;

/**
 * @static
 * @constant
 * @type {number}
 */
Input.keys.X = 88;

/**
 * @static
 * @constant
 * @type {number}
 */
Input.keys.C = 67;

/**
 * @static
 * @constant
 * @type {number}
 */
Input.keys.LEFT = 37;

/**
 * @static
 * @constant
 * @type {number}
 */
Input.keys.RIGHT = 39;

/**
 * @static
 * @constant
 * @type {number}
 */
Input.keys.UP = 38;

/**
 * @static
 * @constant
 * @type {number}
 */
Input.keys.DOWN = 40;

/**
 * @static
 * @constant
 * @type {number}
 */
Input.keys.SPACE = 32;

/**
 * @static
 * @constant
 * @type {number}
 */
Input.keys.ENTER = 13;
